/*
  CoreCommands V2 — Failproof Intent-Based Command Processor
  
  Architecture:
  1. Normalize input (lowercase, strip filler words)
  2. Score against intent keywords (not exact regex)
  3. Extract entities (contact names, deal names, amounts, industries)
  4. Route to best matching command
  5. Graceful AI fallthrough for anything uncertain
*/

// ── NORMALIZE ──
const FILLER = /\b(um|uh|like|actually|basically|so|well|okay|ok|hey|hi|yo|please|can you|could you|would you|will you|go ahead and|i want to|i need to|i'd like to|let me|let's|just|real quick|for me|right now|right away|immediately|also|and also|oh|hmm|ah|er|the|a|an|my|me|i|do|does|did)\b/gi;

function normalize(text) {
  return text.toLowerCase().replace(/[!?.,']+/g, " ").replace(/\s+/g, " ").trim();
}

function stripFiller(text) {
  return text.replace(FILLER, " ").replace(/\s+/g, " ").trim();
}

// ── FUZZY CONTACT MATCHING ──
function fuzzyMatch(query, target) {
  if (!query || !target) return 0;
  const q = query.toLowerCase().trim();
  const t = target.toLowerCase().trim();
  if (t === q) return 1.0;
  if (t.startsWith(q) || t.endsWith(q)) return 0.9;
  if (t.includes(q)) return 0.8;
  // Check each word
  const qWords = q.split(" ");
  const tWords = t.split(" ");
  let matched = 0;
  for (const qw of qWords) {
    if (qw.length < 2) continue;
    for (const tw of tWords) {
      if (tw === qw) { matched += 1; break; }
      if (tw.startsWith(qw) || qw.startsWith(tw)) { matched += 0.7; break; }
      // Levenshtein for typos (simple version)
      if (qw.length >= 3 && tw.length >= 3) {
        let diff = 0;
        const shorter = qw.length < tw.length ? qw : tw;
        const longer = qw.length < tw.length ? tw : qw;
        for (let i = 0; i < shorter.length; i++) {
          if (shorter[i] !== longer[i]) diff++;
        }
        diff += longer.length - shorter.length;
        if (diff <= 2 && shorter.length >= 3) { matched += 0.5; break; }
      }
    }
  }
  return qWords.length > 0 ? matched / qWords.length : 0;
}

function findContact(contacts, query) {
  const matches = findAllContacts(contacts, query);
  return matches.length > 0 ? matches[0] : null;
}

function findAllContacts(contacts, query) {
  if (!query || query.length < 2) return [];
  const q = query.toLowerCase().trim();
  
  // Exact full name match — skip disambiguation entirely
  const exactFull = contacts.filter(c => (c.fn + " " + c.ln).toLowerCase() === q);
  if (exactFull.length === 1) return exactFull;
  
  // Exact first+last combo (case insensitive, trimmed)
  const exactCombo = contacts.filter(c => {
    const full = (c.fn + " " + c.ln).toLowerCase();
    return full === q || full.replace(/\s+/g, " ") === q;
  });
  if (exactCombo.length === 1) return exactCombo;
  
  // Multi-word query with 2+ words = likely a full name attempt
  // Only return contacts where BOTH first AND last name words appear
  const qWords = q.split(/\s+/).filter(w => w.length > 1);
  if (qWords.length >= 2) {
    const fullMatches = contacts.filter(c => {
      const first = (c.fn || "").toLowerCase();
      const last = (c.ln || "").toLowerCase();
      return qWords.some(w => first.includes(w) || first === w) && 
             qWords.some(w => last.includes(w) || last === w);
    });
    if (fullMatches.length >= 1) return fullMatches;
  }

  // Single word query — fuzzy match as before
  const results = [];
  for (const c of contacts) {
    const full = (c.fn + " " + c.ln).toLowerCase();
    const first = (c.fn || "").toLowerCase();
    const last = (c.ln || "").toLowerCase();
    const co = (c.co || "").toLowerCase();
    const scores = [
      fuzzyMatch(q, full) * 1.0,
      fuzzyMatch(q, first) * 0.85,
      fuzzyMatch(q, last) * 0.8,
      fuzzyMatch(q, co) * 0.6,
    ];
    const s = Math.max(...scores);
    if (s >= 0.5) results.push({ contact: c, score: s });
  }
  results.sort((a, b) => b.score - a.score);
  return results.map(r => r.contact);
}

function findDeal(deals, query) {
  if (!query || query.length < 2) return null;
  const q = query.toLowerCase().trim();
  let best = null, bestScore = 0;
  for (const d of deals) {
    const s = fuzzyMatch(q, d.name || "");
    if (s > bestScore && s >= 0.5) { bestScore = s; best = d; }
  }
  return best;
}


/*
  CoreCommands V5 — Action-Only Router
  
  Philosophy: AI handles ALL conversational and informational queries.
  Local intents ONLY fire for explicit UI actions (open X, call Y, create task, navigate).
  Keys are specific multi-word phrases to avoid false matches.
*/

const INTENTS = {
  // ── NAVIGATION: open specific tools ──
  open_dialer: { 
    keys: ["open dialer", "open the dialer", "power dial", "start dialing", "dial pad"],
    action: (ctx) => ({ type: "local", modal: "dialer", speech: `Opening the dialer.${ctx.callQueue.length>0?` ${ctx.callQueue.length} in queue.`:""}` })
  },
  open_email: {
    keys: ["open email", "open my email", "open inbox", "check email", "check my email", "go to email"],
    action: () => ({ type: "local", modal: "email", speech: "Opening your email." })
  },
  open_sms: {
    keys: ["open sms", "open texts", "open messages", "check texts", "check messages", "go to sms"],
    action: () => ({ type: "local", modal: "sms", speech: "Opening SMS." })
  },
  open_calendar: {
    keys: ["open calendar", "open my calendar", "check calendar", "go to calendar", "open schedule"],
    action: () => ({ type: "local", modal: "calendar", speech: "Opening your calendar." })
  },
  open_chat: {
    keys: ["open team chat", "open chat", "go to chat", "team chat"],
    action: () => ({ type: "local", modal: "teamChat", speech: "Opening team chat." })
  },
  open_projects: {
    keys: ["open projects", "go to projects", "project board"],
    action: () => ({ type: "local", modal: "projects", speech: "Opening projects." })
  },
  open_workflows: {
    keys: ["open workflows", "go to workflows", "open automations"],
    action: () => ({ type: "local", modal: "workflows", speech: "Opening workflows." })
  },
  open_call_logs: {
    keys: ["open call logs", "call history", "call logs", "go to call logs"],
    action: () => ({ type: "local", modal: "callLogs", speech: "Opening call logs." })
  },

  // ── EDGE PANELS ──
  show_kpis: {
    keys: ["show my kpis", "show kpis", "open kpis", "my kpis", "my stats", "my numbers", "my metrics"],
    action: (ctx) => {
      const dp = ctx.targets?.dials>0?Math.round((ctx.todayDials/ctx.targets.dials)*100):0;
      return { type: "local", edge: "kpi", speech: `${ctx.todayDials} calls today at ${dp}% of target. Pipeline at $${ctx.pipe?.toLocaleString()||0}.` };
    }
  },
  show_tasks: {
    keys: ["show my tasks", "show tasks", "open tasks", "pull up tasks", "go to tasks", "take me to tasks"],
    action: (ctx) => ({ type: "local", edge: "tasks", speech: `${ctx.overdue.length+ctx.todayTasks.length} tasks need attention. ${ctx.overdue.length} overdue.` })
  },
  show_pipeline: {
    keys: ["show pipeline", "show my pipeline", "open pipeline", "my pipeline", "show me my pipeline", "show me the pipeline", "pipeline view"],
    action: () => ({ type: "navigate", page: "deals", speech: "Here's your deal pipeline." })
  },
  show_deals: {
    keys: ["show deals", "show my deals", "open deals", "my deals", "show me my deals", "show me deals", "deal pipeline"],
    action: () => ({ type: "navigate", page: "deals", speech: "Here are your deals." })
  },
  show_priority: {
    keys: ["show priority", "show my priority", "open priority", "pull up priority", "who should i call", "who to call", "call queue", "show queue"],
    action: () => ({ type: "local", edge: "priority", speech: "Here's your priority list." })
  },
  show_briefing: {
    keys: ["daily briefing", "show briefing", "morning briefing", "give me a briefing", "catch me up", "daily summary"],
    action: () => ({ type: "local", briefing: true, speech: "Here's your daily briefing." })
  },

  // ── CONTACT ACTIONS: things that DO something in the UI ──
  call_contact: {
    keys: ["call", "dial", "ring", "give a call"],
    needsEntity: "contact",
    action: (ctx, ct) => ct ? { type: "local", modal: "dialer", queueContact: ct, callTarget: ct, speech: `Calling ${ct.fn} ${ct.ln}. Opening the dialer.` } : { type: "local", speech: "Who would you like to call?" }
  },
  email_contact: {
    keys: ["email", "send email to", "shoot an email to"],
    needsEntity: "contact",
    notKeys: ["open email", "check email", "open my email", "go to email", "how many email", "emails today", "saying", "tell them", "tell him", "tell her", "with the message", "that says", "draft", "write", "compose"],
    action: (ctx, ct) => ct ? { type: "local", contact: ct, modal: "email", speech: `Opening email for ${ct.fn} ${ct.ln}.` } : { type: "local", modal: "email", speech: "Opening email." }
  },
  text_contact: {
    keys: ["text", "send a text to"],
    needsEntity: "contact",
    notKeys: ["open sms", "open texts", "check texts", "saying", "tell them", "tell him", "tell her", "with the message", "that says"],
    action: (ctx, ct) => ct ? { type: "local", contact: ct, modal: "sms", speech: `Opening SMS for ${ct.fn} ${ct.ln}.` } : { type: "local", modal: "sms", speech: "Opening messages." }
  },
  add_to_queue: {
    keys: ["add to queue", "add to call queue", "queue up", "put in queue"],
    needsEntity: "contact",
    action: (ctx, ct) => ct ? { type: "local", queueContact: ct, speech: `Added ${ct.fn} ${ct.ln} to the call queue.` } : { type: "local", speech: "Who should I add?" }
  },
  show_contact: {
    keys: ["pull up", "look up", "open"],
    needsEntity: "contact_or_deal",
    notKeys: ["open dialer", "open email", "open sms", "open calendar", "open chat", "open projects", "open workflows", "open call logs", "open tasks", "open kpis", "open priority", "open schedule", "open automations", "open inbox"],
    action: (ctx, ct, dl) => {
      if (ct) return { type: "local", contact: ct, speech: `Pulling up ${ct.fn} ${ct.ln}.` };
      if (dl) return { type: "local", deal: dl, speech: `Pulling up ${dl.name}.` };
      return null;
    }
  },
  create_task: {
    keys: ["create task", "create a task", "add task", "add a task", "new task", "make a task", "remind me to", "set a reminder"],
    needsEntity: "contact_optional",
    action: (ctx, ct, dl, raw) => {
      if (ct) return { type: "local", createTask: { title: `Follow up with ${ct.fn} ${ct.ln}`, due: tomorrow(), contactId: ct.id }, speech: `Created a follow-up task for ${ct.fn}.` };
      const title = raw.replace(/^(?:create|add|make|new|set)\s*(?:a\s+)?(?:task|reminder)(?:\s*:\s*|\s+to\s+|\s+)/i, "").trim();
      if (title.length > 3) return { type: "local", createTask: { title, due: today() }, speech: `Created task: ${title}.` };
      return { type: "local", modal: "newTask", speech: "What task would you like to create?" };
    }
  },

  // ── PAGE NAVIGATION ──
  navigate_page: {
    keys: ["take me to", "go to", "navigate to", "switch to"],
    notKeys: ["go to email", "go to sms", "go to chat", "go to calendar", "go to call logs", "go to projects", "go to workflows"],
    action: (ctx, ct, dl, raw) => {
      const pageMap = { people:"contacts", contacts:"contacts", contact:"contacts", deals:"deals", pipeline:"deals", analytics:"analytics", reports:"analytics", lists:"lists", companies:"companies", tasks:"tasks", admin:"admin", settings:"admin" };
      for (const [k,v] of Object.entries(pageMap)) {
        if (raw.toLowerCase().includes(k)) return { type: "navigate", page: v, speech: `Taking you to ${k}.` };
      }
      return null;
    }
  },

  // ── SYSTEM ──
  dismiss: {
    keys: ["dismiss", "never mind", "nevermind", "cancel", "close panel", "close all", "hide panel"],
    action: () => ({ type: "local", closeAll: true, speech: "Done." })
  },
  refresh: {
    keys: ["refresh", "reload", "sync data", "update data"],
    action: () => ({ type: "local", speech: "Refreshing.", refresh: true })
  },
  help: {
    keys: ["help", "what can you do", "what can i say"],
    action: () => ({ type: "local", speech: "I can open any tool, look up contacts and deals, create tasks, call or email someone, and answer questions about your pipeline. Just ask naturally." })
  },
};
function today() { return new Date().toISOString().slice(0,10); }
function tomorrow() { const d=new Date();d.setDate(d.getDate()+1);return d.toISOString().slice(0,10); }

// ── MAIN PROCESSOR ──
export { findAllContacts };
export default function processCommand(text, ctx) {
  const raw = normalize(text);
  const stripped = stripFiller(raw);

  // If the message contains content to compose/send, go straight to AI
  // e.g. "send a text to X saying Y", "email X telling them Y", "draft an email to X about Y"
  const hasContent = /\b(saying|tell them|tell him|tell her|tell .+ that|with the message|that says|draft|compose|write)\b/i.test(raw);
  const isCompose = hasContent && /\b(text|sms|email|message|send)\b/i.test(raw);
  if (isCompose) {
    return {
      type: "ai", speech: null,
      aiPrompt: `User said: "${text}". They want to compose/send a message. Help them draft it with the content they specified. Be concise.`
    };
  }
  
  // Score each intent
  let bestIntent = null, bestScore = 0;
  
  for (const [id, intent] of Object.entries(INTENTS)) {
    let score = 0;
    for (const key of intent.keys) {
      if (raw.includes(key)) score += key.split(" ").length * 2; // multi-word keys score higher
      else if (stripped.includes(key)) score += key.split(" ").length * 1.5;
      else {
        // Check if any individual words from multi-word keys appear
        const words = key.split(" ");
        const matched = words.filter(w => w.length > 2 && (raw.includes(w) || stripped.includes(w)));
        if (matched.length > 0) score += matched.length * 0.5;
      }
    }
    // Penalize if notKeys are present
    if (intent.notKeys) {
      for (const nk of intent.notKeys) {
        if (raw.includes(nk)) score -= 5;
      }
    }
    if (score > bestScore) { bestScore = score; bestIntent = { id, ...intent }; }
  }

  // Threshold: need at least 1.5 to match
  if (!bestIntent || bestScore < 1.5) {
    // Complete fallthrough to AI
    return {
      type: "ai", speech: null,
      aiPrompt: `User said: "${text}". They're a sales professional. Pipeline: $${ctx.pipe?.toLocaleString()||0}, ${ctx.deals.filter(d=>d.stage!=="Closed Won"&&d.stage!=="Closed Lost").length} active deals. Won: $${ctx.won?.toLocaleString()||0}. ${ctx.todayDials} calls, ${ctx.todayEmails} emails today. ${ctx.overdue.length} overdue tasks. Hot leads: ${ctx.hot.slice(0,3).map(c=>c.fn+" "+c.ln).join(", ")}. Help them. Be concise.`
    };
  }

  // Entity extraction
  let contact = null, deal = null, allMatches = [];
  if (bestIntent.needsEntity) {
    // Strip intent keywords to isolate the entity name
    let entityText = raw;
    for (const key of bestIntent.keys) {
      entityText = entityText.replace(new RegExp(key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'), "");
    }
    // Also strip common prepositions and words
    entityText = entityText.replace(/\b(to|for|with|about|on|of|from|at|in|up|the|their|his|her|its|my|our|your|this|that|these|those|is|are|was|were|be|been|being|have|has|had|do|does|did|will|would|could|should|may|might|shall|can)\b/gi, "").replace(/\s+/g, " ").trim();
    
    if (bestIntent.needsEntity === "contact" || bestIntent.needsEntity === "contact_optional" || bestIntent.needsEntity === "contact_or_deal") {
      allMatches = findAllContacts(ctx.contacts, entityText);
      contact = allMatches.length > 0 ? allMatches[0] : null;
      // If multiple matches, return a disambiguation result
      if (allMatches.length > 1) {
        const result = bestIntent.action(ctx, contact, deal, raw);
        if (result) {
          result.multiMatch = allMatches;
          result.intentId = bestIntent.id;
          result.rawText = raw;
        }
        return result || { type: "local", multiMatch: allMatches, intentId: bestIntent.id, rawText: raw, speech: `I found ${allMatches.length} matches. Which one?` };
      }
    }
    if ((!contact && bestIntent.needsEntity === "contact_or_deal") || bestIntent.needsEntity === "deal") {
      deal = findDeal(ctx.deals, entityText);
    }
  }

  // Execute intent action
  const result = bestIntent.action(ctx, contact, deal, raw);
  if (result) return result;

  // If intent matched but action returned null (e.g. no contact found), fall through to AI
  return {
    type: "ai", speech: null,
    aiPrompt: `User said: "${text}". They're a sales professional. Pipeline: $${ctx.pipe?.toLocaleString()||0}. Help them. Be concise.`
  };
}
